import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const Documentation = () => {
  const { t, locale } = useLanguage();
  const [content, setContent] = useState<string>("");

  useEffect(() => {
    document.title = `${t.documentation.title} | ${t.pageTitle.default}`;
    return () => {
      document.title = t.pageTitle.default;
    };
  }, [t, locale]);

  useEffect(() => {
    const loadDoc = async () => {
      setContent(`# ${t.documentation.loading}\n`);
      try {
        // Sempre buscar o arquivo que existe em public/docs (evita SPA devolver index.html)
        const res = await fetch("/docs/documentacao-cadastro.md", {
          cache: "no-store",
          headers: { Accept: "text/markdown, text/plain, */*" },
        });
        const text = await res.text();
        if (!res.ok) {
          setContent(`# ${t.documentation.loadError}`);
          return;
        }
        // Se o servidor devolveu HTML (ex.: SPA fallback), não usar
        const trimmed = text.trim();
        if (
          trimmed.startsWith("<!") ||
          trimmed.startsWith("<html") ||
          trimmed.toLowerCase().includes("<!doctype html")
        ) {
          setContent(`# ${t.documentation.loadError}`);
          return;
        }
        setContent(text);
      } catch {
        setContent(`# ${t.documentation.loadError}`);
      }
    };

    loadDoc();
  }, [t, locale]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="container mx-auto px-4 py-10 max-w-5xl">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 px-1">
          {t.documentation.title}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 px-1">
          {t.documentation.subtitle}
        </p>
        <div className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-xl shadow-sm p-6 md:p-8">
          <div
            className={`
              prose prose-slate max-w-none
              dark:prose-invert
              prose-headings:dark:text-gray-100
              prose-p:dark:text-gray-300
              prose-strong:dark:text-gray-200
              prose-li:dark:text-gray-300
              prose-a:dark:text-sky-400 prose-a:dark:underline
              prose-code:dark:!bg-gray-700 prose-code:dark:!text-gray-200 prose-code:before:dark:content-none prose-code:after:dark:content-none prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:dark:!bg-gray-900 prose-pre:dark:!border-gray-600 prose-pre:dark:border prose-pre:dark:overflow-x-auto
              [&_pre_code]:dark:!bg-transparent [&_pre_code]:dark:!text-gray-200
              prose-blockquote:dark:border-gray-600 prose-blockquote:dark:text-gray-300
              prose-table:dark:text-gray-300
              prose-th:dark:border-gray-600 prose-th:dark:bg-gray-700/50 prose-th:dark:text-gray-200
              prose-td:dark:border-gray-600
            `}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Documentation;
